import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, CardHeader, Col, Container, Row } from "reactstrap";
import { Socket, io } from 'socket.io-client';
import Toastr from 'toastr';
import DaScreenHeader from "../components/Headers/DaScreenHeader";
import { useConversationFileMutation } from '../hooks/useConversationFileMutation';
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useGetConversationByID } from '../hooks/useGetConversationByID';
import { useInfiniteConversation } from '../hooks/useInfiniteConversation';
import { ChatMessages } from "../types";
import { useQueryClient } from "react-query";
import Message from '../components/ChatMessage';
import { Description, RemoveCircle, CameraEnhanceOutlined } from '@material-ui/icons';
import html2canvas from 'html2canvas';

const RESULTS_PER_PAGE = 10;

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessages[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [socketClient, setSocketClient] = useState<Socket | null>(null);
  const [isReady, setReady] = useState<boolean>(false);
  const [fileUrls, setFileUrls] = useState<string[]>([]);
  const [newError, setNewError] = useState<string>("");
  const [fileDetails, setFileDetails] = useState<{ name: string; url: string; }[]>([])
  const [typing, setTyping] = useState<boolean>(false);
  const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false);
  const [isUserMessage, setIsUserMessage] = useState<boolean>(false);
  const [disableSsButton, setDisableSsButton] = useState<boolean>(false)


  const { data: user } = useCurrentUser()
  const { id } = useParams<{ id: string }>()
  const scrollRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient();
  const {
    data: messageData,
    fetchPreviousPage,
    hasPreviousPage,
    isFetched,
    isFetchingPreviousPage
  } = useInfiniteConversation(id, RESULTS_PER_PAGE)

  const {
    data: containerStatus,
  } = useGetConversationByID(id, 500000) //Call every 5 minutes

  useEffect(() => {
    queryClient.removeQueries({
      queryKey: ["conversation", "messages"]
    })
  }, [])

  const {
    mutateAsync: saveConversationsFile,
    data: saveConversationsFileResponse,
  } = useConversationFileMutation();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFileUrls([]);
      setFileDetails([])
      const files = Array.from(event.target.files);
      setUploadedFiles(files);
      if (divRef && divRef.current) {
        divRef.current?.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  };

  const captureScreenshot = async () => {
    try { 
      setDisableSsButton(true);
      if (scrollRef.current) {
        const chatContainer = document.getElementById('messages-ref');
        if(!chatContainer){
          return;
        }
        Toastr.info("Please wait while chat screenshot is getting ready, it will take few seconds!")
        
        // Get the total height of the chat container
        const totalHeight = chatContainer.scrollHeight;
        
        // Set the initial scroll position to the bottom of the chat
        let scrollPosition = totalHeight;
        
        // Scroll and capture screenshots until reaching the top
        while (scrollPosition > 0 && chatContainer.offsetHeight <= 50000) {
          chatContainer.scrollIntoView({
            behavior: 'auto',
          });
          
          // Scroll up by a certain amount (adjust as needed)
          if(scrollPosition >= 10000){
            scrollPosition -= 5000;
          }else if(scrollPosition >= 4000){
            scrollPosition -= 2000;
          }else if(scrollPosition >= 1000){
            scrollPosition -= 500;
          }else if(scrollPosition >= 100){
            scrollPosition -= 200;
          }else{
            scrollPosition -= 50;
          }
          chatContainer.scrollTop = scrollPosition;
          console.log("SCROLL", scrollPosition);
          // Wait for the scrolling to complete
          await new Promise((resolve) => setTimeout(resolve, 500));
          
          // Give some time before the next scroll (adjust as needed)
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        
        // Ensure all images are loaded before taking the screenshot
        const images = chatContainer.getElementsByClassName('img-responsive');
        const imagePromises = Array.from(images).map((image) => {
          return new Promise<void>((resolve) => {
            if ((image as HTMLImageElement).complete) {
              resolve();
            } else {
              (image as HTMLImageElement).onload = () => resolve();
            }
          });
        });
        await Promise.all(imagePromises);
        
        console.log("chatContainer actual height", chatContainer.offsetHeight);
        const canvas = await html2canvas(chatContainer,{ allowTaint: true, useCORS: true});
        console.log("CANVAS", canvas);
  
        // Create an "a" element to download the screenshot
        const link = document.createElement('a');
        link.href = canvas.toDataURL();
        link.download = 'chat_screenshot.png';
        document.body.appendChild(link);
        console.log("LINK",link);
        link.click();
        document.body.removeChild(link);
        setDisableSsButton(false)
      }
    } catch (error) {
      console.log("ERROR from screen shot", error);
    }
  }; 

  useEffect(() => {
    if (saveConversationsFileResponse) {
      setFileUrls(saveConversationsFileResponse)
    }
  }, [saveConversationsFileResponse])

  useEffect(() => {
    if (user && socketClient && !socketClient.connected) {
      socketClient.connect();
    }
    if (!user && socketClient?.connected) {
      socketClient.disconnect();
    }
  }, [user, socketClient]);

  const divRef = useRef<HTMLDivElement>(null);

  const handleSendMessage = async () => {
    setIsUserMessage(true);
    let msg = [...messages];
    if (newMessage.trim() !== "") {
      let finalFileData = [];
      setReady(false);
      if (uploadedFiles.length) {
        const fileRespo = await saveConversationsFile({
          files: uploadedFiles
        })
        if (fileRespo.length) {
          for (let i = 0; i < fileRespo.length; i++) {
            let obj = {
              name: uploadedFiles[i].name,
              url: fileRespo[i]
            };
            finalFileData.push(obj)
          }
          setFileDetails(finalFileData);
          setUploadedFiles([]);
        }
      }
      msg.push({ content: newMessage, role: "user" })
      setMessages(msg);
      setNewMessage("");
      socketClient?.emit("message", {
        content: newMessage,
        role: "user",
        files: finalFileData,
        conversation_id: parseInt(id)
      });
    } else {
      Toastr.info("Input box can't be empty, Please ask something!")
    }
  };

  useEffect(() => {
    setSocketClient(() => {
      const socketUrl = process.env.REACT_APP_WS_CHAT_URL || '/api';
      const client = io(socketUrl.startsWith(`/`) ? window.origin : socketUrl, {
        autoConnect: false,
        path: socketUrl.startsWith(`/`) ? socketUrl : ``,
        transports: ["websocket"]
      });
      return client;
    });
  }, []);

  function removeFileHandle(fileIndex: number) {
    const files = [...uploadedFiles];

    files.splice(fileIndex, 1);

    setUploadedFiles(files)
  }

  const handleScroll = () => {
    if (!scrollRef.current) {
      return;
    }
    const scrollContainer = scrollRef.current as HTMLDivElement;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    
    const isNearTop = Math.abs(scrollTop) + clientHeight + 100 >= scrollHeight;
    
    if(isNearTop){
      setTimeout(() => {
        fetchPreviousPage();
      }, 500)
    }

    const isScrolledToBottom = (scrollTop === 0);
    setShowScrollToBottomButton(!isScrolledToBottom);
  };

  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }
    const scrollContainer = scrollRef.current as HTMLDivElement;
    scrollContainer.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [scrollRef]);

  const scrollToBottom = () => {
    if (!scrollRef.current) {
      return;
    }
    const scrollContainer = scrollRef.current as HTMLDivElement;
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }; 

  useEffect(() => {
    function handleOutputToken(token: string) {
      setTyping(true)
      setNewError("")
      setMessages(oldMsg => {
        let messages = [...oldMsg];
        let lastMessage = messages[messages.length - 1];

        if (lastMessage.role === 'user') {
          messages.push({ content: token, role: "assistant" })
        } else {
          lastMessage.content = lastMessage.content + token;
        }
        return messages;
      });
    }

    function handleError(error: any) {
      if (error) {
        Toastr.error(error)
      } else {
        Toastr.error("Please try again")
      }
      setReady(true)
      setTyping(false)
    }

    function handleConnection() {
      socketClient?.emit(`authentication`, {
        token: localStorage.getItem(`token`),
        id
      });
    }

    function handleUnauthorized() {
      setNewError("Error: You are unauthorized, Please Try again!")
      setReady(false);
      setTyping(false)
    }

    function handleDisconnection() {
      setReady(false);
    }

    function handleAuthenticated() {
      setReady(true);
    }

    function handleDone(res: any) {
      setReady(true);
      setTyping(false);
    }

    if (socketClient) {
      socketClient.on(`connect`, handleConnection);
      socketClient.on(`disconnect`, handleDisconnection);
      socketClient.on(`authenticated`, handleAuthenticated);
      socketClient.on(`unauthorized`, handleUnauthorized);
      socketClient.on(`token`, handleOutputToken);
      socketClient.on(`error`, handleError);
      socketClient.on(`done`, handleDone);
    }

    return () => {
      if (!socketClient) return;
      socketClient.disconnect();
      socketClient.off(`connect`, handleConnection);
      socketClient.off(`disconnect`, handleDisconnection);
      socketClient.off(`authenticated`, handleAuthenticated);
      socketClient.off(`unauthorized`, handleUnauthorized);
      socketClient.off(`token`, handleOutputToken);
      socketClient.off(`error`, handleError);
      socketClient.off(`done`, handleDone);
    };
  }, [socketClient]);

  useEffect(() => {
    const messageContainer = document.getElementById("message-container");
    if (messageContainer) {
      messageContainer.scrollTop = messageContainer.scrollHeight;
    }
  }, [isUserMessage]);

  return (
    <>
      <div >
        <DaScreenHeader />

        <Container className="mt--7 mb-3" fluid>
          <Row className="mt-5">
            <Col className="mb-5 mb-xl-0">
              <Card className="shadow">
                <CardHeader className="border-0">
                  <Row>
                  <Col xs={9}>
                      <h3>
                        {containerStatus?.name + "      "}
                        <span className="status-light-blink" style={{ backgroundColor: containerStatus?.containerStatus === 'running' ? 'green' : 'red' }} />
                      </h3>
                    </Col>
                    <Col xs={3} className="text-right">
                      {disableSsButton ? (
                          <svg style={{float: "right"}} height="60px" viewBox="0 0 187.3 93.7" preserveAspectRatio="xMidYMid meet" >
                            <path stroke="rgb(215, 61, 50)" id="outline" fill="none" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10"
                              d="M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z" />
                            <path id="outline-bg" opacity="0.05" fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10"
                              d="				M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z" />
                          </svg>): (
                          <Button 
                          onClick={captureScreenshot}
                          style={{
                            backgroundColor: 'snow',
                          }}
                          title="Take Full Chat Screenshot" 
                          >
                            < CameraEnhanceOutlined />
                          </Button>
                      )}  
                    </Col>
                  </Row>
                </CardHeader>
                <div
                  id="message-container"
                  ref={scrollRef}
                  style={{ height: "65vh", 
                  overflow: "auto", 
                  display: "flex",
                  flexDirection: "column-reverse",
                  position: "relative", 
                  overflowAnchor: "none",
                  backgroundColor: "#F5F5F5",
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  fontFamily: 'roboto',
                  }}
                >
                  <div id="messages-ref" className="fetched-messages">
                    {isFetchingPreviousPage && <div style={{textAlign: "center"}}>
                      <span>loading</span>
                    </div>}
                    {messageData?.pages.map((page) => {
                      return page.messages.slice().reverse().map((message) => (
                        <Message typing={typing} index={message.id} content={message.content} role={message.role} messageLength={messages.length} />
                      ))
                    })}

                    {messages.map((message, index) => (
                      <Message typing={typing} index={index} content={message.content} role={message.role} messageLength={messages.length} />
                    ))}
                    
                  </div>
                </div>

                {newError ? <div
                  style={{
                    backgroundColor: '#CD8D8C',
                    alignItems: "flex-start",
                    padding: "10px 24% 5px 24%",
                  }}
                >
                  <h3>{newError}</h3>
                </div> : ""}

                <div className="chat-form">
                  {!fileUrls.length && uploadedFiles.length > 0 && (
                    <div className="uploaded-files-section" style={{
                      backgroundColor: "#F5F5F5",
                      color: "#ECECF1",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "flex-start",
                      whiteSpace: "pre-line",
                      paddingLeft: "20%",
                      paddingRight: "30%",
                    }}>
                      <ul style={{
                        listStyleType: "none", marginBottom: "0",
                        backgroundColor: "#CFD8DC", width: "100%",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                        padding: "0",
                        paddingLeft: "3px",
                        paddingBottom: "3px",
                        paddingTop: "3px",
                        boxShadow: "#78909c 0px 1px 3px, #78909c 0px 1px 2px",
                        maxHeight: "70px",
                        overflowY: "auto"
                      }}>
                        {uploadedFiles.map((fle, index) => (
                          <li style={{ display: "inline-block", marginRight: '10px' }} key={index}>
                            <a style={{ color: "#222" }} target="_blank"><Description />&nbsp;{fle.name}&nbsp;
                            </a>
                            <a href={undefined} onClick={() => removeFileHandle(index)}><RemoveCircle htmlColor="#DD2C00" /></a>

                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {fileDetails.length > 0 && (
                    <div className="uploaded-files-section" style={{
                      backgroundColor: "#F5F5F5",
                      color: "#ECECF1",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "flex-start",
                      whiteSpace: "pre-line",
                      paddingLeft: "20%",
                      paddingRight: "30%",
                    }}>
                      <ul style={{
                        listStyleType: "none", marginBottom: "0",
                        backgroundColor: "#CFD8DC", width: "100%",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                        padding: "0",
                        paddingLeft: "3px",
                        paddingBottom: "3px",
                        paddingTop: "3px",
                        boxShadow: "#78909c 0px 1px 3px, #78909c 0px 1px 2px", color: "#222"
                      }}>
                        {fileDetails.map((fle, index) => {
                          return (
                            <li style={{ display: "inline-block", marginRight: '10px' }} key={index}>
                              {/* TODO: have token appended in react query hook (data can be transformed using `select` option) */}
                              <a style={{ color: "#222" }} href={`${fle.url}?token=${localStorage.getItem('token')}`} target="_blank" rel="noopener noreferrer">
                                <Description />{fle.name}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {containerStatus?.containerStatus === 'running' &&
                    <div style={{
                      display: "flex",
                      justifyContent: "center",
                      paddingLeft: '10px',
                      backgroundColor: "#F5F5F5",
                    }}>
                      <textarea
                        // disabled={!isReady}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Send a message..."
                        style={{
                          flex: "0.65",
                          marginRight: "8px",
                          marginBottom: "10px",
                          minHeight: "30px",
                          borderRadius: '6px',
                          backgroundColor: "#fff",
                          border: '1px solid #EEEEEE',
                          boxShadow: "rgba(50, 50, 105, 0.15) 0px 2px 5px 0px, rgba(0, 0, 0, 0.05) 0px 1px 1px 0px",
                          padding: '8px',
                          color: '#222',
                          resize: "vertical"
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            if(isReady){
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }
                        }}
                      />

                      <Button
                        disabled={!isReady}
                        onClick={handleSendMessage}
                        style={{ height: '40px', padding: '10px', marginTop: '15px', marginRight: '10px' }}
                      >
                        <i className="fas fa-paper-plane"></i>
                      </Button>

                      <input
                        disabled={!isReady}
                        type="file"
                        multiple
                        accept=".csv, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        id="file-input"
                      />
                      <label htmlFor="file-input" style={{ cursor: 'pointer', padding: '10px', marginTop: '15px', color: '#181817', backgroundColor: '#eeeee4', border: '1px solid #6666', borderRadius: '6px', height: '41px' }}>
                        <i className="fas fa-upload"></i>
                      </label>
                    </div>
                  }
                  {typing || showScrollToBottomButton ?
                    <Button
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToBottom();
                        }}          
                        style={{
                          position: 'absolute',
                          bottom: '100px',
                          width: '52px',
                          height: '40px',
                          borderRadius: '35%',
                          backgroundColor: 'transparent',
                          marginLeft: '48%',
                          marginRight: '450%',
                        }}
                    >
                      <i className="fas fa-arrow-down"></i>
                    </Button>: ""
                  }
                </div>
              </Card>
              <div ref={divRef}>&nbsp;</div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Chat;
